/**
 * Hook to resolve player names for activity feed items via Apollo cache or network query.
 * Prevents player name truncation (e.g., "Virgil van Dijk" displayed as just "van")
 * by fetching full name data (firstName, knownName, lastName) from the GraphQL API.
 * For note activities where metadata.playerId is missing, fetches the note first
 * to extract the playerId before resolving the player name (SE-3990).
 * @module hooks/useActivityPlayerNames
 */

import { gql, useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import {
  ActivityFeedFragment,
  ActivityFeedGetPlayerNameDocument,
  ActivityFeedGetPlayerNameQuery,
  NotesGetNoteDocument,
  type NotesGetNoteQuery,
  type NotesGetNoteQueryVariables,
} from "@/generated/graphql";
import { isNoteActivity } from "@/utils/isNoteActivity";
import { UNKNOWN_PLAYER } from "@/utils/playerNameUtils";

/**
 * Activity type codes that need current player name resolution from the GraphQL API.
 * MANUAL_PLAYER_* types are excluded because they use the backend's activity.title
 * which contains the historical player name at the time of the event.
 * CONTENT_SHARED is handled dynamically via isNoteActivity when contentType is "NOTE".
 */
const PLAYER_ACTIVITY_CODES = new Set([
  "NOTE_CREATED",
  "NOTE_SHARED",
  "CONTENT_SHARED",
  "SCOUT_REPORT_CREATED",
  "SCOUT_REPORT_SHARED",
]);

/**
 * Fragment for reading player name data from Apollo cache.
 * Matches the fields requested by ActivityFeedGetPlayerName query — the
 * backend-canonical `name` field (SE-5469).
 */
const ACTIVITY_PLAYER_NAME_FRAGMENT = gql`
  fragment ActivityPlayerName on Player {
    id
    name
  }
`;

/**
 * Extracts player IDs and note IDs from activity items that need player name resolution.
 * For note activities without a metadata playerId, captures the targetId (note ID)
 * so the note can be fetched to resolve the player.
 * @param activities - Array of activity feed fragments
 * @returns Object with playerIds to resolve and noteTargetIds to fetch
 */
const extractActivityInfo = (
  activities: readonly ActivityFeedFragment[]
): {
  readonly playerIds: readonly string[];
  readonly noteTargetIds: readonly string[];
} => {
  const infos = activities.reduce(
    (acc, activity) => {
      const code = activity.activity.activityType.code;
      if (!PLAYER_ACTIVITY_CODES.has(code)) return acc;

      const metadata = activity.activity.metadata as
        | Record<string, unknown>
        | null
        | undefined;
      const noteActivity = isNoteActivity(code, metadata);

      // Skip non-note CONTENT_SHARED (e.g. report shares) — their targetId
      // is not a player ID and would trigger bogus getPlayer lookups
      if (code === "CONTENT_SHARED" && !noteActivity) return acc;

      const metadataPlayerId = metadata?.playerId as string | undefined;

      // For note activities, targetId is the note ID — not a player ID (SE-3989)
      const playerId = noteActivity
        ? metadataPlayerId
        : metadataPlayerId || activity.activity.targetId;

      if (playerId) {
        return {
          ...acc,
          playerIds: { ...acc.playerIds, [playerId]: true as const },
        };
      }

      // No playerId in metadata — if it's a note activity, capture noteId to fetch later
      // For CONTENT_SHARED, metadata.noteId may differ from targetId (SE-3990)
      const noteId = noteActivity
        ? (metadata?.noteId as string | undefined) || activity.activity.targetId
        : undefined;

      if (noteId) {
        return {
          ...acc,
          noteTargetIds: { ...acc.noteTargetIds, [noteId]: true as const },
        };
      }

      return acc;
    },
    {
      playerIds: {} as Record<string, true>,
      noteTargetIds: {} as Record<string, true>,
    }
  );

  return {
    playerIds: Object.keys(infos.playerIds),
    noteTargetIds: Object.keys(infos.noteTargetIds),
  };
};

/** Return value from useActivityPlayerNames */
interface UseActivityPlayerNamesReturn {
  /** Map of player ID → display name */
  readonly playerNames: Record<string, string>;
  /** Map of note ID → player ID for note activities missing metadata.playerId (SE-3990) */
  readonly noteIdToPlayerIdMap: Record<string, string>;
}

/**
 * Resolves player display names for activity feed items by first checking the
 * Apollo cache and then querying the network for any cache misses.
 * For note activities where metadata lacks a playerId, fetches the note
 * to extract the playerId before resolving the player name (SE-3990).
 * @param activities - Array of activity feed fragments to resolve player names for
 * @returns playerNames map and noteIdToPlayerIdMap for note activities
 */
const useActivityPlayerNames = (
  activities: readonly ActivityFeedFragment[] | undefined
): UseActivityPlayerNamesReturn => {
  // 1. VARIABLES & STATE
  const client = useApolloClient();
  const [networkNames, setNetworkNames] = useState<Record<string, string>>({});
  const [notePlayerIds, setNotePlayerIds] = useState<Record<string, string>>(
    {}
  );

  const { playerIds, noteTargetIds } = useMemo(
    () => extractActivityInfo(activities ?? []),
    [activities]
  );

  /** Combine directly-available playerIds with those resolved from notes */
  const allPlayerIds = useMemo(
    () => [...new Set([...playerIds, ...Object.values(notePlayerIds)])],
    [playerIds, notePlayerIds]
  );

  // Read available names from Apollo cache
  const cachedNames = useMemo(() => {
    return allPlayerIds.reduce(
      (acc, id) => {
        const fragment = client.readFragment<{
          id: string;
          name: string;
        }>({
          id: `Player:${id}`,
          fragment: ACTIVITY_PLAYER_NAME_FRAGMENT,
        });

        if (!fragment) return acc;

        const displayName = fragment.name ?? UNKNOWN_PLAYER;
        return { ...acc, [id]: displayName };
      },
      {} as Record<string, string>
    );
  }, [allPlayerIds, client]);

  // Identify IDs not found in cache or already resolved via network
  const missingIds = useMemo(
    () => allPlayerIds.filter(id => !cachedNames[id] && !networkNames[id]),
    [allPlayerIds, cachedNames, networkNames]
  );

  // Merge cached and network-resolved names
  const playerNames = useMemo(
    () => ({ ...cachedNames, ...networkNames }),
    [cachedNames, networkNames]
  );

  // 2. SIDE EFFECTS
  // Fetch notes to resolve playerIds for note activities missing metadata.playerId
  useEffect(() => {
    if (noteTargetIds.length === 0) return;

    const fetchNotePlayerIds = async () => {
      const results = await Promise.allSettled(
        noteTargetIds.map(noteId =>
          client.query<NotesGetNoteQuery, NotesGetNoteQueryVariables>({
            query: NotesGetNoteDocument,
            variables: { id: noteId },
          })
        )
      );

      const resolved = results.reduce(
        (acc, result, index) => {
          if (result.status !== "fulfilled") return acc;

          const playerId = result.value.data?.getNote?.playerId;
          if (!playerId) return acc;

          return { ...acc, [noteTargetIds[index]]: playerId };
        },
        {} as Record<string, string>
      );

      if (Object.keys(resolved).length > 0) {
        setNotePlayerIds(prev => ({ ...prev, ...resolved }));
      }
    };

    fetchNotePlayerIds().catch(error => {
      console.warn("Failed to fetch note player IDs for activity feed:", error);
    });
  }, [noteTargetIds, client]);

  // Fetch missing player names from the network
  useEffect(() => {
    if (missingIds.length === 0) return;

    const fetchMissingNames = async () => {
      const results = await Promise.allSettled(
        missingIds.map(id =>
          client.query<ActivityFeedGetPlayerNameQuery>({
            query: ActivityFeedGetPlayerNameDocument,
            variables: { id },
          })
        )
      );

      const resolved = results.reduce(
        (acc, result, index) => {
          if (result.status !== "fulfilled") return acc;

          const player = result.value.data?.getPlayer;
          if (!player) return acc;

          const displayName = player.name ?? UNKNOWN_PLAYER;
          return { ...acc, [missingIds[index]]: displayName };
        },
        {} as Record<string, string>
      );

      if (Object.keys(resolved).length > 0) {
        setNetworkNames(prev => ({ ...prev, ...resolved }));
      }
    };

    fetchMissingNames().catch(error => {
      console.warn("Failed to fetch player names for activity feed:", error);
    });
  }, [missingIds, client]);

  // 3. RETURN
  return { playerNames, noteIdToPlayerIdMap: notePlayerIds };
};

export default useActivityPlayerNames;
